import { AxiosError } from 'axios'
import { toast } from 'sonner'

/**
 * Custom API Error class with structured error information
 */
export class ApiError extends Error {
  code: string
  status: number
  details?: Record<string, string[]>
  /**
   * Raw response body, kept so callers can read endpoint-specific fields the
   * generic shape drops (e.g. `allowed_types` / `max_size_mb` on the payout
   * document endpoints). Undefined for network/timeout errors.
   */
  payload?: unknown
  timestamp: string

  constructor(
    message: string,
    code: string,
    status: number,
    details?: Record<string, string[]>,
    payload?: unknown
  ) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.status = status
    this.details = details
    this.payload = payload
    this.timestamp = new Date().toISOString()
  }

  /**
   * Check if this is a validation error (has field-level details)
   */
  isValidationError(): boolean {
    return this.code === 'VALIDATION_ERROR' && !!this.details
  }

  /**
   * Check if this is an authentication error
   */
  isAuthError(): boolean {
    return ['TOKEN_EXPIRED', 'TOKEN_INVALID', 'SESSION_EXPIRED'].includes(this.code)
  }

  /**
   * Check if this is a permission error
   */
  isPermissionError(): boolean {
    return this.code === 'PERMISSION_DENIED'
  }

  /**
   * Get field-level error messages
   */
  getFieldErrors(): Record<string, string> {
    if (!this.details) return {}
    
    const fieldErrors: Record<string, string> = {}
    for (const [field, messages] of Object.entries(this.details)) {
      fieldErrors[field] = messages.join(', ')
    }
    return fieldErrors
  }
}

/**
 * Error codes and their human-readable messages
 */
export const ERROR_MESSAGES: Record<string, string> = {
  // Authentication errors
  TOKEN_EXPIRED: 'Your session has expired. Please log in again.',
  TOKEN_INVALID: 'Invalid session. Please log in again.',
  SESSION_EXPIRED: 'Your session has expired. Please log in again.',
  INVALID_CREDENTIALS: 'Invalid verification code. Please try again.',
  USER_NOT_FOUND: 'No account found with this phone number.',
  
  // Permission errors
  PERMISSION_DENIED: 'You do not have permission to perform this action.',
  
  // Rate limiting
  RATE_LIMITED: 'Too many requests. Please wait a moment and try again.',
  
  // Validation errors
  VALIDATION_ERROR: 'Please check your input and try again.',

  // Upload errors
  UNSUPPORTED_MEDIA_TYPE: 'That file type is not supported.',
  PAYLOAD_TOO_LARGE: 'The file is too large to upload.',
  
  // Server errors
  SERVICE_ERROR: 'Something went wrong. Please try again later.',
  SERVER_ERROR: 'Server error. Please try again later.',
  
  // Network errors
  NETWORK_ERROR: 'Unable to connect. Please check your internet connection.',
  TIMEOUT_ERROR: 'Request timed out. Please try again.',
  
  // Business logic errors
  ORDER_NOT_FOUND: 'Order not found.',
  ORDER_ALREADY_CANCELLED: 'This order has already been cancelled.',
  ORDER_CANNOT_CANCEL: 'This order cannot be cancelled.',
  PROVIDER_NOT_AVAILABLE: 'The selected provider is not available.',
  INSUFFICIENT_BALANCE: 'Insufficient balance for this transaction.',
  
  // Default
  UNKNOWN_ERROR: 'An unexpected error occurred. Please try again.',
}

/**
 * Get human-readable error message for an error code
 */
export function getErrorMessage(code: string): string {
  return ERROR_MESSAGES[code] || ERROR_MESSAGES.UNKNOWN_ERROR
}

/**
 * Extract error information from various error response formats
 */
interface ErrorResponseData {
  error?: string
  message?: string
  detail?: string
  error_code?: string
  code?: string
  details?: Record<string, string[]>
  success?: boolean
}

/**
 * Handle API errors from Axios and convert to ApiError
 */
export function handleApiError(error: AxiosError): ApiError {
  // Handle network errors (no response)
  if (!error.response) {
    if (error.code === 'ECONNABORTED') {
      return new ApiError(
        getErrorMessage('TIMEOUT_ERROR'),
        'TIMEOUT_ERROR',
        0
      )
    }
    return new ApiError(
      getErrorMessage('NETWORK_ERROR'),
      'NETWORK_ERROR',
      0
    )
  }

  const raw = error.response.data
  // A non-JSON body (e.g. an nginx HTML error page) has no fields to read.
  const response = (raw && typeof raw === 'object' ? raw : undefined) as ErrorResponseData | undefined
  const status = error.response.status

  const code =
    response?.error_code ||
    response?.code ||
    getStatusErrorCode(status)

  // Prefer the server's own text; otherwise use the most specific message we
  // have for the code, which beats a generic "unexpected error" for 413/415.
  const message =
    response?.error ||
    response?.message ||
    response?.detail ||
    ERROR_MESSAGES[code] ||
    getErrorMessage('UNKNOWN_ERROR')

  const details = response?.details

  return new ApiError(message, code, status, details, raw)
}

/**
 * Get error code based on HTTP status code
 */
function getStatusErrorCode(status: number): string {
  switch (status) {
    case 400:
      return 'VALIDATION_ERROR'
    case 401:
      return 'TOKEN_INVALID'
    case 403:
      return 'PERMISSION_DENIED'
    case 404:
      return 'NOT_FOUND'
    case 413:
      return 'PAYLOAD_TOO_LARGE'
    case 415:
      return 'UNSUPPORTED_MEDIA_TYPE'
    case 422:
      return 'VALIDATION_ERROR'
    case 429:
      return 'RATE_LIMITED'
    case 500:
    case 502:
    case 503:
    case 504:
      return 'SERVER_ERROR'
    default:
      return 'UNKNOWN_ERROR'
  }
}

/**
 * Show toast notification for an error
 * 
 * @param error - The error to display (ApiError, Error, or string)
 * @param options - Optional configuration for the toast
 */
export function showErrorToast(
  error: ApiError | Error | string,
  options?: {
    title?: string
    duration?: number
    action?: {
      label: string
      onClick: () => void
    }
  }
): void {
  let message: string
  let title = options?.title || 'Error'

  if (error instanceof ApiError) {
    message = error.message
    
    // Customize title based on error type
    if (error.isAuthError()) {
      title = 'Session Error'
    } else if (error.isPermissionError()) {
      title = 'Access Denied'
    } else if (error.isValidationError()) {
      title = 'Validation Error'
    }
  } else if (error instanceof Error) {
    message = error.message
  } else {
    message = error
  }

  toast.error(title, {
    description: message,
    duration: options?.duration || 5000,
    action: options?.action
      ? {
          label: options.action.label,
          onClick: options.action.onClick,
        }
      : undefined,
  })
}

/**
 * Show toast notification for validation errors with field details
 */
export function showValidationErrorToast(error: ApiError): void {
  if (!error.isValidationError() || !error.details) {
    showErrorToast(error)
    return
  }

  const fieldErrors = Object.entries(error.details)
    .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
    .join('\n')

  toast.error('Validation Error', {
    description: fieldErrors || error.message,
    duration: 7000,
  })
}

/**
 * Show success toast notification
 */
export function showSuccessToast(
  message: string,
  options?: {
    title?: string
    duration?: number
  }
): void {
  toast.success(options?.title || 'Success', {
    description: message,
    duration: options?.duration || 3000,
  })
}

/**
 * Show info toast notification
 */
export function showInfoToast(
  message: string,
  options?: {
    title?: string
    duration?: number
  }
): void {
  toast.info(options?.title || 'Info', {
    description: message,
    duration: options?.duration || 4000,
  })
}

/**
 * Show warning toast notification
 */
export function showWarningToast(
  message: string,
  options?: {
    title?: string
    duration?: number
  }
): void {
  toast.warning(options?.title || 'Warning', {
    description: message,
    duration: options?.duration || 5000,
  })
}
